/**
 * Cart drawer upsell sheet.
 *
 * <cart-drawer-upsell> is rendered once inside #CartDrawerForm (snippets/cart-drawer.liquid). It is a bottom
 * sheet that covers the drawer contents. The buttons that open it (snippets/cart-drawer-upsell-button.liquid)
 * sit under eligible cart items and are re-rendered on every cart rebuild; the sheet content for each
 * metaobject entry lives in a <template data-cart-drawer-upsell-template="<handle>"> inside the rebuilt
 * `.cart__items`, so it is cloned fresh on every open and always reflects the current cart.
 *
 * Everything generic (data JSON, colours, sizes, helper, add to cart with `_upsell_for*` properties) comes from
 * the shared assets/post-atc-upsell-core.js. Unlike the product-page popup there is no session rule and no
 * `ajaxProduct:added` tracking; the trigger is a click on a drawer button. After a successful add the core
 * dispatches `cart:updated` + `cart:build` (the drawer re-renders, the trigger disappears because the item is
 * now linked) and the sheet closes while the drawer stays open.
 */
(function () {
  'use strict';

  var selectors = {
    trigger: '[data-cart-drawer-upsell-open]',
    close: '[data-cart-drawer-upsell-close]',
    sheet: '[data-cart-drawer-upsell-sheet]',
    content: '[data-cart-drawer-upsell-content]',
    template: 'template[data-cart-drawer-upsell-template]'
  };

  var SHEET_FOCUS_NAMESPACE = 'cart_drawer_upsell_focus';
  var DRAWER_FOCUS_NAMESPACE = 'cartdrawer_focus';

  function defineElement() {
    if (customElements.get('cart-drawer-upsell')) return;
    if (!window.PostAtcUpsellCore) {
      console.warn('cart-drawer-upsell: core script (post-atc-upsell-core.js) is missing');
      return;
    }

    class CartDrawerUpsell extends window.PostAtcUpsellCore {
      constructor() {
        super();

        this.trigger = null;

        this._onDocumentClick = this._onDocumentClick.bind(this);
        this._onKeyup = this._onKeyup.bind(this);
      }

      connectedCallback() {
        this.sheet = this.querySelector(selectors.sheet);
        this.content = this.querySelector(selectors.content);
        this.modal = this.sheet;

        // Delegation: overlay and the close button inside the (replaced) sheet content
        this.addEventListener('click', (event) => {
          if (event.target.closest(selectors.close)) this.close();
        });

        // Delegation: triggers are re-rendered on every cart rebuild
        document.addEventListener('click', this._onDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._onDocumentClick);
        window.removeEventListener('keyup', this._onKeyup, true);
      }

      _onDocumentClick(event) {
        if (!(event.target instanceof Element)) return;

        var trigger = event.target.closest(selectors.trigger);
        if (trigger) this.openFor(trigger);
      }

      /*
        Clones the template of the trigger's entry into the sheet, binds it to the parent cart item
        (size + colour preselection, `_upsell_for*` properties) and opens the sheet.
      */
      openFor(trigger) {
        if (!this.content) return;

        var handle = trigger.dataset.entry;
        var template = Array.from(document.querySelectorAll(selectors.template)).find((el) => {
          return el.dataset.cartDrawerUpsellTemplate === handle;
        });
        if (!template) return;

        this.content.replaceChildren(document.importNode(template.content, true));
        if (!this.bindBody(this.content)) return;

        var dataset = trigger.dataset;
        var parent = {
          key: dataset.parentKey,
          variant_id: dataset.parentVariantId,
          product_title: dataset.parentProductTitle,
          variant_title: dataset.parentVariantTitle,
          options_with_values: this._parseJson(dataset.parentOptions, [])
        };
        this.parentItem = parent;

        // Size conversions of the cart item's own product (the body is rendered once per entry, not per item)
        this.data.sizeConversion = this.data.sizeConversion || {};
        this.data.sizeConversion.entries = this._parseJson(dataset.sizeEntries, {});

        // Preselect the upsell size closest to the cart item's size, then the closest colour
        this.targetSize = this._addedSize(parent);
        if (this.targetSize) {
          this._rebuildOptions(true);
          this._renderHelper();
        }
        this._preselectColor(parent.product_title);

        this.trigger = trigger;
        this.open();
        // Safari does not focus buttons on click, so document.activeElement may not be the trigger
        this.previousFocus = trigger;
      }

      _parseJson(value, fallback) {
        try {
          return value ? JSON.parse(value) : fallback;
        } catch (error) {
          return fallback;
        }
      }

      /*
        Focus trap + Escape while the sheet is open (hooks of the core open()/close()). The drawer closes on
        window `keyup` Escape, so Escape is handled on keyup in the capture phase and stopped, which closes
        only the sheet.
      */
      _activate() {
        theme.a11y.trapFocus({
          container: this.sheet,
          elementToFocus: this.sheet,
          namespace: SHEET_FOCUS_NAMESPACE
        });
        window.addEventListener('keyup', this._onKeyup, true);
      }

      _deactivate() {
        window.removeEventListener('keyup', this._onKeyup, true);
        theme.a11y.removeTrapFocus({
          container: this.sheet,
          namespace: SHEET_FOCUS_NAMESPACE
        });

        // Hand the focus trap back to the drawer (the sheet trap replaced it); focus the trigger if it survived
        var form = this.closest('form');
        if (form && document.documentElement.classList.contains('js-drawer-open')) {
          var focusEl = this.trigger && this.trigger.isConnected ? this.trigger : form;
          theme.a11y.trapFocus({
            container: form,
            elementToFocus: focusEl,
            namespace: DRAWER_FOCUS_NAMESPACE
          });
        }
        this.trigger = null;
      }

      _onKeyup(event) {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        this.close(false);
      }
    }

    customElements.define('cart-drawer-upsell', CartDrawerUpsell);
  }

  // Core is deferred before this script; fall back to DOMContentLoaded if the order was ever different
  if (window.PostAtcUpsellCore) {
    defineElement();
  } else {
    document.addEventListener('DOMContentLoaded', defineElement);
  }
})();
